import React, { Component } from 'react';
import { Form, Col, Input, Row, Select, Checkbox, InputNumber } from "antd";
import { useSelector } from 'react-redux';
import Scrollbar from '../../tools/common/Scrollbar';
import { SEAT_LABEL_OPTIONS, SeatDistribution } from '../../../enums/SeatDistributionEnum'
import { ActiveSelectionPropertiesEnum, ObjectSuperTypesEnum, SELECTION_OPTIONS } from '../../../enums';
import { tPropertyBar, tRow, tTable } from '../../../assets/libs/i18n/translator';
import { ROW_TABLE_LABEL_OPTIONS, RowTableDistributionEnum } from '../../../enums/RowTableDistributionEnum';
import { RowTableStartsAtInput } from './RowTableStartsAtInput';
import { StartsAtInput } from './StartsAtInput';
import { RowSpacingHelper } from '../../../canvas/designer/helpers/RowSpacingHelper';

const ActiveSelectionContent = ({ form, getFieldValue, selectedItem }) => {

	const { Option } = Select;
	const { Item } = Form;
	const categories = [{ key: 0, name: 'Seleccione categoría' }, ...useSelector(state => state.properties.categories)];
	const objects = selectedItem._objects;

	const hasOnlyObjectsOfType = (objects, superType) => {
		return objects.length > 0 && objects.every((obj) => obj.superType === superType);
	};

	const onlyRows = hasOnlyObjectsOfType(objects, ObjectSuperTypesEnum.Row);
	const onlyRectTables = hasOnlyObjectsOfType(objects, ObjectSuperTypesEnum.RectTable);
	const onlyRoundTables = hasOnlyObjectsOfType(objects, ObjectSuperTypesEnum.RoundTable);
	const onlyTables = objects.length > 0 && objects.every((obj) =>
		[
			ObjectSuperTypesEnum.RectTable,
			ObjectSuperTypesEnum.RoundTable,
		].includes(obj.superType)
	);
	const hasRowsOrTables = objects.some((obj) =>
		[
			ObjectSuperTypesEnum.Row,
			ObjectSuperTypesEnum.RectTable,
			ObjectSuperTypesEnum.RoundTable,
		].includes(obj.superType)
	);

	const renderNumber = (key, label, initialValue, props = {}, span) => (
		<Col span={span}>
			<Item label={label} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
				{form(key, {
					initialValue,
				})(<InputNumber {...props} />)}
			</Item>
		</Col>
	);

	const renderCheckbox = (key, label, initialValue) => (
		<Item label={label} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
			{form(key, {
				valuePropName: "checked",
				initialValue,
			})(<Checkbox />)}
		</Item>
	);

	const renderSelect = (key, label, initialValue, options, span) => (
		<Col span={span}>
			<Item label={label} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
				{form(key, {
					initialValue,
				})(
					<Select>
						{options.map((opt) => (
							<Option key={opt.value ?? opt.key} value={opt.value ?? opt.key}>
								{opt.label ?? opt.name}
							</Option>
						))}
					</Select>
				)}
			</Item>
		</Col>
	);

	const renderTitle = (label) => (
		<Col span={24} className="mb-1 mt-2">
			<h3><b>{label}</b></h3>
		</Col>
	);

	const rowLabelDistribution =
		getFieldValue?.(ActiveSelectionPropertiesEnum.RowLabel) ?? RowTableDistributionEnum.NONE;
	const tableLabelDistribution =
		getFieldValue?.(ActiveSelectionPropertiesEnum.TableLabel) ?? RowTableDistributionEnum.NONE;
	const seatLabelingDistribution =
		getFieldValue?.(ActiveSelectionPropertiesEnum.SeatLabeling) ?? SeatDistribution.NONE;

	const renderRowTableStartsAt = (distributionKey) => (
		<Col span={24}>
			<Item label={tPropertyBar('startsAt')} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
				{form(ActiveSelectionPropertiesEnum.RowTableStartsAt, {
					initialValue: 1,
				})(
					<RowTableStartsAtInput distributionKey={distributionKey} />
				)}
			</Item>
		</Col>
	);

	return (
		<ul className="rde-ul">
			<Row>

				{
					hasRowsOrTables &&
					<>
						{renderTitle(tPropertyBar('category'))}

						<Col span={24}>
							<Item wrapperCol={{ span: 24 }}>
								{form(ActiveSelectionPropertiesEnum.Categories, {
									initialValue: 0
								})(
									<Select>
										{categories.map(cat => (
											<Select.Option key={cat.key} value={cat.key}>
												{cat.name}
											</Select.Option>
										))}
									</Select>
								)}
							</Item>
						</Col>
					</>
				}
				{
					onlyRectTables &&
					<>
						{renderTitle(tPropertyBar('seats'))}

						<Col span={24}>
							<Row gutter={4}>
								{renderNumber(ActiveSelectionPropertiesEnum.SeatsUp, tTable("up"), 0, { min: 0 }, 12)}
								{renderNumber(ActiveSelectionPropertiesEnum.SeatsDown, tTable("down"), 0, { min: 0 }, 12)}
								{renderNumber(ActiveSelectionPropertiesEnum.SeatsLeft, tTable("left"), 0, { min: 0 }, 12)}
								{renderNumber(ActiveSelectionPropertiesEnum.SeatsRight, tTable("right"), 0, { min: 0 }, 12)}
							</Row>
						</Col>
					</>
				}
				{
					onlyRoundTables &&
					<>
						{renderTitle(tPropertyBar('seats'))}
						{renderNumber(ActiveSelectionPropertiesEnum.RoundTableSeatsQuantity, tPropertyBar("seatsQuantity"), 0, { min: 0 }, 24)}
						{renderNumber(ActiveSelectionPropertiesEnum.RoundTableEmptySpaces, tTable("emptySpaces"), 0, { min: 0 }, 24)}
					</>
				}

				{
					onlyRows &&
					<>
						{objects.length > 1 && renderNumber(ActiveSelectionPropertiesEnum.RowSpacing,
							tRow('rowsSpacing'), RowSpacingHelper.getSpacing(selectedItem), { min: 0 }, 24)}
						{renderTitle(tPropertyBar('seats'))}
						{renderNumber(ActiveSelectionPropertiesEnum.RowSeatsQuantity, tPropertyBar("seatsQuantity"), 0, { min: 0 }, 24)}
						{renderNumber(ActiveSelectionPropertiesEnum.RowSeatSpacing, tRow("seatsSpacing"), 0, { min: 0 }, 24)}
						{renderNumber(ActiveSelectionPropertiesEnum.RowCurve, tRow("curve"), 0, { min: 0 }, 24)}
					</>
				}
				{
					onlyRoundTables &&
					<>
						{renderTitle(tTable("tableSize"))}
						{renderNumber(ActiveSelectionPropertiesEnum.TableRadius, tTable("radius"), 0, { min: 10 }, 24)}
					</>
				}
				{
					onlyRectTables &&
					<>
						{renderTitle(tTable("tableSize"))}
						{renderNumber(ActiveSelectionPropertiesEnum.TableWidth, tTable("width"), 0, { min: 10 }, 24)}
						{renderNumber(ActiveSelectionPropertiesEnum.TableHeight, tTable("heigth"), 0, { min: 10 }, 24)}
					</>
				}
				{
					hasRowsOrTables &&
					<>
						{renderTitle(tPropertyBar('sectionLabeling'))}
						<Col span={24}>
							<Item label={tPropertyBar('label')} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
								{form(ActiveSelectionPropertiesEnum.SectionName, {
									initialValue: ''
								})(
									<Input />
								)}
							</Item>

							<Item label={tPropertyBar('displayedLabel')} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
								{form(ActiveSelectionPropertiesEnum.DisplayedSectionName, {
									initialValue: ''
								})(
									<Input />
								)}
							</Item>
						</Col>
					</>
				}
				{
					onlyRows &&
					<>
						{renderTitle(tRow('rowLabeling'))}
						{renderSelect(ActiveSelectionPropertiesEnum.RowLabel, tPropertyBar('label'), 0, ROW_TABLE_LABEL_OPTIONS, 24)}
						{rowLabelDistribution !== RowTableDistributionEnum.NONE &&
							renderRowTableStartsAt(rowLabelDistribution)}
						<Col span={24}>
							{renderCheckbox(ActiveSelectionPropertiesEnum.LabelPositionLeft, 'Posición izquierda', false)}
							{renderCheckbox(ActiveSelectionPropertiesEnum.LabelPositionRight, 'Posición derecha', false)}
						</Col>
					</>
				}
				{
					onlyTables &&
					<>
						{renderTitle(tPropertyBar('tableLabeling'))}
						{renderSelect(ActiveSelectionPropertiesEnum.TableLabel, tPropertyBar('label'), 0, ROW_TABLE_LABEL_OPTIONS, 24)}
						{tableLabelDistribution !== RowTableDistributionEnum.NONE &&
							renderRowTableStartsAt(tableLabelDistribution)}
					</>
				}
				{
					hasRowsOrTables &&
					<>
						{renderTitle(tPropertyBar('seatLabeling'))}
						{renderSelect(ActiveSelectionPropertiesEnum.SeatLabeling, tPropertyBar('distribution'), 0, SEAT_LABEL_OPTIONS, 24)}
						{seatLabelingDistribution !== SeatDistribution.NONE &&
							<Col span={24}>
								<Item label={tPropertyBar('startsAt')} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
									{form(ActiveSelectionPropertiesEnum.StarstAt, {
										initialValue: 1,
									})(
										<StartsAtInput distributionKey={seatLabelingDistribution} />
									)}
								</Item>
							</Col>
						}
					</>
				}
				{
					onlyRows &&
					<Col span={24} className='mb-2'>
						{renderCheckbox(ActiveSelectionPropertiesEnum.Reverse, tRow('reverse'), false)}
					</Col>
				}
				{
					onlyTables &&
					<>
						{renderTitle(tTable("miscellaneous"))}
						{renderSelect(ActiveSelectionPropertiesEnum.TableSelectionType, tTable('selectionType'), 1, SELECTION_OPTIONS, 24)}
					</>
				}
			</Row>
		</ul >
	);
};

class ActiveSelectionPropertiesPanel extends Component {

	render() {
		const { selectedItem, form, canvasRef } = this.props;
		const { getFieldDecorator, getFieldValue } = form;
		return (
			<Scrollbar>
				<Form layout="horizontal" colon={false}>
					<ActiveSelectionContent
						form={getFieldDecorator}
						getFieldValue={getFieldValue}
						selectedItem={selectedItem}
						canvasRef={canvasRef}
					/>
				</Form>
			</Scrollbar>
		);
	}
}

export default Form.create({
	onValuesChange: (props, changedValues, allValues) => {
		const { onChange, selectedItem } = props;

		const key = Object.keys(changedValues)[0];
		const value = changedValues[key];
		onChange(selectedItem, key, value, allValues);
	},
})(ActiveSelectionPropertiesPanel);
